import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isWithinInterval } from 'date-fns';
import { PjmRegistrationDto } from 'src/domain/pjm/dto/PjmRegistrationDto';
import { PjmRegistrationStatus } from 'src/domain/pjm/dto/enums';

dayjs.extend(utc);

export function isRegistrationActionAllowed(
    registration: PjmRegistrationDto,
    action: 'submit' | 'withdraw' | 'terminate' | 'save' | 'calculate_wpl' | 'generate_meter_data_file'
): boolean {
    /**
     * we should not allow submitting, withdrawing or terminating registration
     * if it has not been created at DRHUB
     */
    const isCreatedAtDrhub = registration?.id;

    const actionMap: Record<
        PjmRegistrationStatus,
        {
            submit: boolean;
            withdraw: boolean;
            terminate: boolean;
            save: boolean;
            edit: boolean;
            calculate_wpl: boolean;
            generate_meter_data_file: boolean;
        }
    > = {
        [PjmRegistrationStatus.NEW]: {
            submit: true,
            withdraw: false,
            terminate: false,
            save: true,
            edit: true,
            calculate_wpl: true,
            generate_meter_data_file: true,
        },
        [PjmRegistrationStatus.PENDING_PJM]: {
            submit: false,
            withdraw: true,
            terminate: false,
            save: false,
            edit: false,
            calculate_wpl: false,
            generate_meter_data_file: false,
        },
        [PjmRegistrationStatus.CONFIRMED_EXTENSION]: {
            submit: false,
            withdraw: false,
            terminate: true,
            save: true,
            edit: true,
            calculate_wpl: false,
            generate_meter_data_file: false,
        },
        [PjmRegistrationStatus.CONFIRMED]: {
            submit: false,
            withdraw: false,
            terminate: true,
            save: true,
            edit: true,
            calculate_wpl: false,
            generate_meter_data_file: false,
        },
        [PjmRegistrationStatus.DENIED]: {
            // todo: need to clarify
            submit: true,
            withdraw: false,
            terminate: false,
            save: true,
            edit: true,
            calculate_wpl: false,
            generate_meter_data_file: false,
        },
        [PjmRegistrationStatus.WITHDRAWN]: {
            submit: true, // resubmit
            withdraw: false,
            terminate: false,
            save: true,
            edit: true,
            calculate_wpl: true,
            generate_meter_data_file: true,
        },
        [PjmRegistrationStatus.TERMINATED_DUPLICATE]: {
            // todo: need to clarify
            submit: false,
            withdraw: false,
            terminate: false,
            save: false,
            edit: false,
            calculate_wpl: false,
            generate_meter_data_file: false,
        },
        [PjmRegistrationStatus.TERMINATED]: {
            submit: false,
            withdraw: false,
            terminate: false,
            save: false,
            edit: false,
            calculate_wpl: false,
            generate_meter_data_file: false,
        },
    };

    const status = registration?.summaryInfo?.status;

    if (!status) {
        return false;
    }

    const state = actionMap[status as PjmRegistrationStatus];
    if (!state) {
        return false;
    }

    if (!isCreatedAtDrhub && action !== 'save') {
        return false;
    }

    // for registrations without product we should hide calculate WPL button
    if (action === 'calculate_wpl' && !registration?.capacityInfo?.product) {
        return false;
    }

    // for registrations from old season we should hide generate meter data file button
    if (action === 'generate_meter_data_file') {
        const dateFromNextSeason = dayjs.utc().add(1, 'year').toDate();

        if (!isWithinInterval(dateFromNextSeason, { start: registration.summaryInfo.startDate!, end: registration.summaryInfo.endDate! })) {
            return false;
        }
    }

    return state[action];
}
